import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "@/utils/axios";
import { useAuth } from "@/context/Auth";
import { normalizeProduct } from "@/utils/helper/product";
import { useAppDispatch } from "@/store/hooks";
import { addToCart as addToCartAction } from "@/store/slices/cartSlice";
import { formatCurrency } from "@/utils/format";

export default function RecentlyViewedProducts() {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchRecentlyViewed = async () => {
      try {
        if (user) {
          // ✅ Sync guest localStorage into DB once
          const local =
            JSON.parse(localStorage.getItem("recentlyViewed")) || [];
          if (local.length > 0) {
            await Promise.all(
              local.map((p) =>
                axios.post("/products/recently-viewed", {
                  productId: p.productId,
                })
              )
            );
            localStorage.removeItem("recentlyViewed");
          }

          // ✅ Fetch from backend
          const res = await axios.get("/products/recently-viewed");
          setProducts(res.data.map(normalizeProduct));
        } else {
          // fallback for guests
          const local =
            JSON.parse(localStorage.getItem("recentlyViewed")) || [];
          setProducts(local.map(normalizeProduct));
        }
      } catch (err) {
        console.error("Error fetching recently viewed:", err);
      }
    };

    fetchRecentlyViewed();
  }, [user]);

  const handleAddToCart = (product) => {
    const hasDiscount = product.discount > 0;
    const finalPrice = hasDiscount
      ? product.price - (product.price * product.discount) / 100
      : product.price;

    dispatch(
      addToCartAction({
        product: {
          ...product,
          productId: product.productId,
          price: Number(finalPrice), // ✅ discounted price
          image: product.image,
        },
        quantity: 1,
        user,
      })
    );
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800">
            You Might Also Like
          </h2>
          <p className="text-gray-600">
            Explore other products you've recently viewed or might be interested
            in.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => {
            const hasDiscount = product.discount > 0;
            const discountedPrice = hasDiscount
              ? product.price - (product.price * product.discount) / 100
              : product.price;
            const savings = hasDiscount
              ? (product.price * product.discount) / 100
              : 0;

            return (
              <Card
                key={product.productId}
                className="overflow-hidden shadow-sm hover:shadow-md transition-shadow pt-0"
              >
                <CardContent className="p-0 relative">
                  <Link to={`/products/${product.productId}`}>
                    <img
                      src={product.image}
                      alt={product.name}
                      width={200}
                      height={200}
                      className="w-full h-48 object-cover"
                    />
                    {hasDiscount && (
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-xs sm:text-sm font-bold px-2 py-1 rounded-md shadow-md">
                        -{product.discount}%
                      </span>
                    )}
                  </Link>
                  <div className="p-4">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(product.rating)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                      <span className="text-sm text-gray-500 ml-2">
                        ({product.reviews})
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-800 mb-1">
                      <Link
                        to={`/products/${product.productId}`}
                        className="hover:underline"
                      >
                        {product.name}
                      </Link>
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {product.description}
                    </p>

                    {/* ✅ Discount + Savings */}
                    <div className="flex items-center justify-between">
                      {hasDiscount ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500 line-through">
                            {formatCurrency(product.price)}
                          </span>
                          <span className="text-lg sm:text-xl font-bold text-red-600">
                            {formatCurrency(discountedPrice)}
                          </span>
                          <span className="text-xs text-green-600 font-medium">
                            You save {formatCurrency(savings)} ({product.discount}
                            %)
                          </span>
                        </div>
                      ) : (
                        <span className="text-xl font-bold text-gray-900">
                          {formatCurrency(product.price)}
                        </span>
                      )}

                      <Button
                        onClick={() => handleAddToCart(product)}
                        className="bg-primary hover:bg-primary/90 text-white"
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
